import {
  Building2,
  Users,
  ClipboardCheck,
  AlertTriangle,
  Brain,
  FileSearch,
  BookOpenCheck,
  ShieldCheck,
  Database,
  GraduationCap,
  Lock,
  Activity,
  BarChart3,
  Route,
  CheckCircle2,
  ArrowRight,
  PenTool,
  Settings2,
  Target,
  UserCheck,
  BriefcaseBusiness,
} from "lucide-react";

import { Link } from "react-router-dom";

import SEO from "@/components/site/SEO";
import CTASection from "@/components/site/CTASection";
import FAQSection from "@/components/site/FAQSection";

const ORGANISATION_OFFERS = [
  {
    icon: BriefcaseBusiness,
    title: "Bespoke railway training",
    subtitle: "Designed around your people, risk profile and operational needs",
    body:
      "UKTDA designs and delivers bespoke railway training for organisations that need practical, safety-critical learning built around real operational risk. Training can support recruitment, onboarding, competence development, CPD, incident learning, human factors, degraded working and professional development.",
    points: [
      "Bespoke course design for your organisation",
      "Scenario-based operational learning",
      "Human factors, NTS and decision-making content",
      "Training aligned to local risks, roles and standards",
    ],
    to: "/contact",
    buttonLabel: "Discuss bespoke training",
  },
  {
    icon: GraduationCap,
    title: "TRAC-Learn",
    subtitle: "Dedicated LMS and Course Builder",
    body:
      "TRAC-Learn gives organisations a dedicated learning management system for railway training, CPD, briefings, assessments and evidence. The Course Builder allows structured learning content to be created, delivered, tracked and evidenced across teams, depots and roles.",
    points: [
      "Dedicated LMS for railway learning and CPD",
      "Course Builder for organisation-specific content",
      "Knowledge checks, assessments and completion records",
      "Learner progress and training evidence in one place",
    ],
    to: "/trac-learn",
    buttonLabel: "Explore TRAC-Learn",
  },
  {
    icon: Database,
    title: "TRAC-CMS",
    subtitle: "Dedicated Competence Management System",
    body:
      "TRAC-CMS helps organisations manage safety-critical competence records, assessments, reviews, development actions and assurance evidence. It is designed to support clearer visibility of competence status, outstanding actions and evidence quality.",
    points: [
      "Competence records and assessment evidence",
      "Manager reviews and development actions",
      "Role, depot and team-level visibility",
      "Audit-ready evidence for assurance activity",
    ],
    to: "/trac-cms",
    buttonLabel: "Explore TRAC-CMS",
  },
  {
    icon: Brain,
    title: "RAT-BAT",
    subtitle: "Safety-critical recruitment augmentation and support",
    body:
      "RAT-BAT supports safety-critical recruitment by adding structured behavioural risk insight to the selection process. It helps organisations explore risk maturity, rule attitude, task judgement, situational awareness and non-technical skills before or during recruitment.",
    points: [
      "Recruitment support for safety-critical roles",
      "Risk maturity and task judgement insight",
      "NTS and behavioural risk mapping",
      "Interview prompts and candidate comparison",
    ],
    to: "/ratbat",
    buttonLabel: "Explore RAT-BAT",
  },
];

const USE_CASES = [
  {
    icon: UserCheck,
    title: "Recruit safer, better-prepared candidates",
    body:
      "Use RAT-BAT to add structured behavioural risk evidence to recruitment, helping selection teams explore judgement, rule attitude, risk appetite and operational suitability.",
  },
  {
    icon: BookOpenCheck,
    title: "Deliver consistent organisational learning",
    body:
      "Use TRAC-Learn to deliver standardised courses, briefings, CPD, onboarding, refresher training and operational updates with completion evidence.",
  },
  {
    icon: PenTool,
    title: "Build your own internal courses",
    body:
      "Use the Course Builder to create learning content specific to your organisation, depot, route, traction, rules, local risks or internal processes.",
  },
  {
    icon: ClipboardCheck,
    title: "Manage safety-critical competence",
    body:
      "Use TRAC-CMS to record assessments, reviews, evidence, development plans, competence status and management interventions.",
  },
  {
    icon: AlertTriangle,
    title: "Turn incidents into learning",
    body:
      "Convert incidents, near misses, safety events and operational trends into meaningful learning, reflection, competence actions and evidence.",
  },
  {
    icon: BarChart3,
    title: "Improve management visibility",
    body:
      "Give managers clearer visibility of recruitment risk, learning completion, competence status, outstanding actions and assurance gaps.",
  },
];

const TRAINING_EXAMPLES = [
  {
    icon: Activity,
    title: "VR and immersive training",
    body:
      "Create safe, controlled access to operational environments, traction equipment, depots, routes, cabs, lineside risk and scenario-based decision making.",
    points: [
      "Cab, traction and equipment familiarisation",
      "Procedures and risk exposure without live operational risk",
      "Suitable for onboarding, refresher training and CPD",
    ],
    to: "/vr-traction",
    buttonLabel: "See VR training example",
  },
  {
    icon: ShieldCheck,
    title: "Safety-critical learning",
    body:
      "Develop training around operational risk, degraded working, communication, workload management, situational awareness and decision making.",
    points: [
      "Human factors and non-technical skills",
      "Operational thinking and risk-based decisions",
      "Scenario-led learning for safety-critical roles",
    ],
    to: "/elearning",
    buttonLabel: "See eLearning example",
  },
  {
    icon: Route,
    title: "Route, traction and local risk",
    body:
      "Support route learning, traction knowledge, local instructions, depot risks and organisation-specific operational requirements.",
    points: [
      "Route and traction knowledge support",
      "Local risk and procedure awareness",
      "Evidence linked to learning and competence",
    ],
    to: "/contact",
    buttonLabel: "Discuss a training need",
  },
];

const PLATFORM_CONNECTION = [
  {
    icon: Brain,
    title: "1. Assess",
    label: "RAT-BAT",
    body:
      "Add structured behavioural risk insight to recruitment or development by assessing risk maturity, task judgement, rule attitude and non-technical skills.",
  },
  {
    icon: GraduationCap,
    title: "2. Train",
    label: "TRAC-Learn",
    body:
      "Deliver learning, CPD, briefings, knowledge checks and bespoke courses through a dedicated LMS and Course Builder.",
  },
  {
    icon: Database,
    title: "3. Evidence",
    label: "TRAC-CMS",
    body:
      "Record competence evidence, assessments, reviews, development actions and assurance activity in a dedicated competence management system.",
  },
  {
    icon: BarChart3,
    title: "4. Assure",
    label: "TRAC ecosystem",
    body:
      "Use management information to identify risk trends, gaps, outstanding actions, learning needs and assurance priorities.",
  },
];

const SUPPORT_SERVICES = [
  {
    icon: Settings2,
    title: "Implementation support",
    body:
      "Support to help organisations introduce TRAC-Learn, TRAC-CMS, RAT-BAT or a connected TRAC approach, including setup, workflows, role design and adoption.",
  },
  {
    icon: Target,
    title: "Competence framework review",
    body:
      "Review existing competence arrangements, assessment evidence, development actions, manager consistency and assurance visibility.",
  },
  {
    icon: Users,
    title: "Manager, instructor and assessor development",
    body:
      "Develop the people responsible for training, assessing, coaching and managing safety-critical staff.",
  },
  {
    icon: FileSearch,
    title: "Audit and evidence improvement",
    body:
      "Improve the structure, quality and accessibility of evidence used for competence, training, assurance and governance activity.",
  },
  {
    icon: AlertTriangle,
    title: "Incident learning support",
    body:
      "Support the conversion of incidents, trends and near misses into learning, reflection, competence actions and measurable improvement.",
  },
  {
    icon: Lock,
    title: "Governance and data controls",
    body:
      "Support platform governance, access control, audit trails, controlled records and ISO 27001-aligned information security thinking.",
  },
];

const SECURITY_POINTS = [
  "Role-based access control for different user groups",
  "Audit trails to support assurance and accountability",
  "Controlled records for learning, competence and recruitment activity",
  "Clear data ownership and information governance",
  "ISO 27001-aligned information security thinking",
  "Support for compliance, risk management and policy governance",
];

const ORGANISATION_FAQS = [
  {
    q: "What does UKTDA offer organisations?",
    a:
      "UKTDA offers four main organisational services: bespoke railway training, TRAC-Learn as a dedicated LMS and Course Builder, TRAC-CMS as a dedicated competence management system, and RAT-BAT for safety-critical recruitment augmentation and support.",
  },
  {
    q: "Can we use one service without using the full TRAC ecosystem?",
    a:
      "Yes. Organisations can use bespoke training, TRAC-Learn, TRAC-CMS or RAT-BAT separately. They can also be connected to create a stronger end-to-end model covering recruitment insight, learning, competence and assurance.",
  },
  {
    q: "What is TRAC-Learn?",
    a:
      "TRAC-Learn is a dedicated learning management system for railway organisations. It supports online learning, CPD, briefings, assessments, learner progress, completion records and organisation-specific course building.",
  },
  {
    q: "What is TRAC-CMS?",
    a:
      "TRAC-CMS is a dedicated competence management system. It supports competence records, assessments, evidence capture, manager reviews, development actions, assurance visibility and audit-ready compliance evidence.",
  },
  {
    q: "What is RAT-BAT?",
    a:
      "RAT-BAT stands for Risk and Task Based Aptitude Test. It is designed to support safety-critical recruitment by assessing risk maturity, rule attitude, task judgement, situational awareness and non-technical skills through railway-specific scenarios.",
  },
  {
    q: "Can UKTDA build bespoke training for our organisation?",
    a:
      "Yes. UKTDA can design bespoke training around your operational risks, role requirements, local procedures, competence needs, recruitment challenges or professional development objectives.",
  },
  {
    q: "Does TRAC replace employer responsibilities?",
    a:
      "No. TRAC supports organisational training, competence, recruitment and assurance arrangements. It does not replace the legal, regulatory or safety responsibilities of railway undertakings, infrastructure managers, training providers or assessment bodies.",
  },
];

export default function ForOrganisations() {
  return (
    <>
      <SEO
        title="For Organisations | Railway Training, LMS, CMS and Recruitment Support | UKTDA"
        description="UKTDA supports rail organisations with bespoke training, TRAC-Learn LMS and Course Builder, TRAC-CMS competence management, and RAT-BAT safety-critical recruitment augmentation."
        path="/for-organisations"
      />

      <section
        className="relative overflow-hidden bg-navy py-20 md:py-28 text-white"
        data-testid="organisations-hero"
      >
        <div className="container-rail">
          <div className="grid gap-10 lg:grid-cols-[1fr_360px] lg:items-center">
            <div>
              <p className="label-eyebrow text-white/70">For organisations</p>

              <h1 className="mt-4 font-display font-bold text-4xl md:text-6xl leading-tight text-white">
                Railway training, learning, competence and recruitment support
                for safety-critical organisations.
              </h1>

              <p className="mt-6 max-w-3xl text-lg md:text-xl leading-relaxed text-white/80">
                UKTDA helps rail organisations strengthen how they recruit,
                train, develop, evidence and assure safety-critical capability.
                We offer bespoke training, a dedicated LMS and Course Builder,
                a dedicated competence management system and structured
                recruitment support through RAT-BAT.
              </p>

              <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 max-w-5xl">
                {[
                  "Bespoke training",
                  "TRAC-Learn LMS",
                  "TRAC-CMS",
                  "RAT-BAT recruitment support",
                ].map((item) => (
                  <div
                    key={item}
                    className="rounded-md border border-white/10 bg-white/5 px-4 py-3 text-sm font-bold text-white/90"
                  >
                    {item}
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center rounded-md bg-safety px-6 py-3 text-sm font-bold text-white hover:bg-safety/90 transition-colors"
                  data-testid="organisations-hero-contact"
                >
                  Discuss organisational support
                </Link>

                <Link
                  to="/trac-learn"
                  className="inline-flex items-center justify-center rounded-md border border-white/20 px-6 py-3 text-sm font-bold text-white hover:bg-white/10 transition-colors"
                  data-testid="organisations-hero-trac-learn"
                >
                  Explore TRAC-Learn
                </Link>
              </div>
            </div>

            <div className="flex justify-start lg:justify-end">
              <div className="flex h-40 w-40 md:h-52 md:w-52 items-center justify-center rounded-2xl bg-white p-5 shadow-sm ring-1 ring-white/20">
                <img
                  src="/TRAC.svg"
                  alt="TRAC logo"
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        className="py-20 md:py-28 bg-white"
        data-testid="organisation-offer-summary"
      >
        <div className="container-rail">
          <div className="max-w-4xl">
            <p className="label-eyebrow text-safety">What we offer</p>

            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              Four clear ways UKTDA can support your organisation.
            </h2>

            <p className="mt-5 text-slate-600 leading-relaxed">
              Our organisational offer is built around practical railway
              performance: how people are selected, trained, assessed,
              developed and evidenced in safety-critical environments. You can
              use a single service or combine them into a connected TRAC model.
            </p>
          </div>

          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            {ORGANISATION_OFFERS.map((offer, index) => {
              const Icon = offer.icon;

              return (
                <article
                  key={offer.title}
                  className="rounded-md border border-slate-200 bg-slate-50 p-6 md:p-8 shadow-sm hover:border-safety/60 hover:bg-white transition-all"
                  data-testid={`organisation-offer-${index}`}
                >
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-md bg-navy text-white">
                    <Icon className="h-6 w-6" />
                  </div>

                  <p className="mt-5 text-xs font-bold uppercase tracking-wide text-safety">
                    {offer.subtitle}
                  </p>

                  <h3 className="mt-2 font-display font-bold text-2xl text-navy">
                    {offer.title}
                  </h3>

                  <p className="mt-3 text-slate-600 leading-relaxed">
                    {offer.body}
                  </p>

                  <div className="mt-6 grid gap-3 sm:grid-cols-2">
                    {offer.points.map((point) => (
                      <div
                        key={point}
                        className="flex items-start gap-3 rounded-md bg-white p-4 border border-slate-200"
                      >
                        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-safety" />
                        <p className="text-sm text-slate-700">{point}</p>
                      </div>
                    ))}
                  </div>

                  <Link
                    to={offer.to}
                    className="mt-6 inline-flex items-center justify-center gap-2 rounded-md bg-safety px-5 py-3 text-sm font-bold text-white hover:bg-safety/90 transition-colors"
                    data-testid={`organisation-offer-link-${index}`}
                  >
                    {offer.buttonLabel}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section
        className="py-20 md:py-28 bg-slate-50"
        data-testid="organisation-use-cases"
      >
        <div className="container-rail">
          <div className="max-w-4xl">
            <p className="label-eyebrow text-safety">Organisational use cases</p>

            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              Built for operators, training providers and safety-critical teams
              that need stronger control, consistency and evidence.
            </h2>

            <p className="mt-5 text-slate-600 leading-relaxed">
              UKTDA support can help organisations improve recruitment insight,
              training delivery, competence assurance, incident learning and
              management visibility.
            </p>
          </div>

          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {USE_CASES.map((item, index) => {
              const Icon = item.icon;

              return (
                <div
                  key={item.title}
                  className="rounded-md border border-slate-200 bg-white p-6 shadow-sm hover:border-safety/60 transition-all"
                  data-testid={`organisation-use-case-${index}`}
                >
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-navy text-white">
                    <Icon className="h-5 w-5" />
                  </div>

                  <h3 className="mt-4 font-display font-bold text-lg text-navy">
                    {item.title}
                  </h3>

                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    {item.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section
        className="py-20 md:py-28 bg-white"
        data-testid="platform-connection"
      >
        <div className="container-rail">
          <div className="max-w-4xl">
            <p className="label-eyebrow text-safety">How the platform connects</p>

            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              From recruitment insight to learning, competence and assurance.
            </h2>

            <p className="mt-5 text-slate-600 leading-relaxed">
              The strongest organisational value comes when recruitment,
              learning and competence are connected. RAT-BAT identifies risk and
              behavioural themes. TRAC-Learn delivers targeted learning.
              TRAC-CMS records competence and evidence. Management information
              then supports assurance, intervention and continuous improvement.
            </p>
          </div>

          <div className="mt-10 grid gap-5 lg:grid-cols-4">
            {PLATFORM_CONNECTION.map((step, index) => {
              const Icon = step.icon;

              return (
                <div
                  key={step.title}
                  className="rounded-md border border-slate-200 bg-slate-50 p-6 shadow-sm"
                  data-testid={`platform-connection-step-${index}`}
                >
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-md bg-navy text-white">
                    <Icon className="h-6 w-6" />
                  </div>

                  <p className="mt-5 text-xs font-bold uppercase tracking-wide text-safety">
                    {step.label}
                  </p>

                  <h3 className="mt-2 font-display font-bold text-2xl text-navy">
                    {step.title}
                  </h3>

                  <p className="mt-3 text-sm leading-relaxed text-slate-600">
                    {step.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section
        className="py-20 md:py-28 bg-slate-50"
        data-testid="training-examples"
      >
        <div className="container-rail">
          <div className="max-w-4xl">
            <p className="label-eyebrow text-safety">Bespoke training examples</p>

            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              Training designed around operational reality, not generic
              classroom content.
            </h2>

            <p className="mt-5 text-slate-600 leading-relaxed">
              UKTDA can support organisations with immersive, online and
              scenario-based learning that reflects the realities of railway
              operations, safety-critical decision making and professional
              competence.
            </p>

            <div
              className="mt-10 overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm"
              data-testid="kuula-vr-embed"
            >
              <iframe
                title="UKTDA immersive railway training example"
                width="100%"
                height="640"
                frameBorder="0"
                allow="xr-spatial-tracking; gyroscope; accelerometer"
                allowFullScreen
                scrolling="no"
                src="https://kuula.co/share/collection/7lsWj?logo=1&info=0&fs=1&vr=1&sd=1&initload=0&thumbs=1"
                className="block w-full"
              />
            </div>
          </div>

          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            {TRAINING_EXAMPLES.map((course, index) => {
              const Icon = course.icon;

              return (
                <article
                  key={course.title}
                  className="group rounded-md border border-slate-200 bg-white p-6 md:p-8 shadow-sm hover:-translate-y-1 hover:border-safety/70 hover:shadow-lg transition-all"
                  data-testid={`training-example-${index}`}
                >
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-md bg-navy text-white group-hover:bg-safety transition-colors">
                    <Icon className="h-6 w-6" />
                  </div>

                  <h3 className="mt-5 font-display font-bold text-2xl text-navy">
                    {course.title}
                  </h3>

                  <p className="mt-3 text-sm leading-relaxed text-slate-600">
                    {course.body}
                  </p>

                  <div className="mt-6 grid gap-3">
                    {course.points.map((point) => (
                      <div
                        key={point}
                        className="flex items-start gap-3 rounded-md bg-slate-50 p-4"
                      >
                        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-safety" />
                        <p className="text-sm text-slate-700">{point}</p>
                      </div>
                    ))}
                  </div>

                  <Link
                    to={course.to}
                    className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-safety px-5 py-3 text-sm font-bold text-white shadow-sm ring-1 ring-safety/20 transition-all hover:-translate-y-0.5 hover:bg-safety/90 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-safety focus:ring-offset-2"
                    data-testid={`training-example-link-${index}`}
                  >
                    {course.buttonLabel}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section
        className="py-20 md:py-28 bg-white"
        data-testid="support-services"
      >
        <div className="container-rail">
          <div className="max-w-4xl">
            <p className="label-eyebrow text-safety">
              Consultancy and implementation support
            </p>

            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              Support with the platform, the people and the process.
            </h2>

            <p className="mt-5 text-slate-600 leading-relaxed">
              UKTDA can support organisations beyond the software itself. We can
              help shape workflows, improve evidence quality, develop managers
              and assessors, review competence frameworks and turn operational
              learning into structured improvement.
            </p>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {SUPPORT_SERVICES.map((service, index) => {
              const Icon = service.icon;

              return (
                <div
                  key={service.title}
                  className="bg-white border border-slate-200 rounded-md p-6 hover:border-safety/60 hover:shadow-sm transition-all"
                  data-testid={`support-service-${index}`}
                >
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-navy text-white">
                    <Icon className="h-5 w-5" />
                  </div>

                  <p className="mt-4 font-display font-bold text-navy text-lg">
                    {service.title}
                  </p>

                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                    {service.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section
        className="py-20 md:py-28 bg-navy text-white"
        data-testid="security-governance"
      >
        <div className="container-rail">
          <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
            <div>
              <p className="label-eyebrow text-white/70">
                Security, governance and assurance
              </p>

              <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl leading-tight">
                Designed for organisations that need control, accountability
                and evidence.
              </h2>

              <p className="mt-5 text-white/80 leading-relaxed">
                Training, competence and recruitment records are sensitive
                organisational evidence. TRAC is positioned around controlled
                access, audit trails, information governance and ISO
                27001-aligned thinking so that organisations can build trust in
                the platform and the evidence it holds.
              </p>

              <p className="mt-4 text-white/80 leading-relaxed">
                This matters across all connected areas: RAT-BAT assessment
                data, TRAC-Learn training records, TRAC-CMS competence evidence
                and the management information used for assurance decisions.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {SECURITY_POINTS.map((point) => (
                <div
                  key={point}
                  className="rounded-md border border-white/10 bg-white/5 p-4"
                >
                  <div className="flex items-start gap-3">
                    <Lock className="mt-0.5 h-5 w-5 shrink-0 text-white" />
                    <p className="text-sm text-white/85 leading-relaxed">
                      {point}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 rounded-md border border-white/10 bg-white/5 p-6 md:p-8">
            <p className="font-display text-xl font-bold">
              ISO 27001-aligned positioning
            </p>

            <p className="mt-3 text-white/80 leading-relaxed">
              TRAC can be developed and described using ISO 27001-aligned
              principles, including information security management, access
              control, audit evidence, risk management, supplier assurance,
              incident management, policy governance and controlled records.
            </p>
          </div>
        </div>
      </section>

      <FAQSection
        items={ORGANISATION_FAQS}
        title="Organisational support FAQs"
        intro="Common questions about UKTDA’s organisational offer, including bespoke training, TRAC-Learn, TRAC-CMS and RAT-BAT recruitment support."
        schemaId="faq-for-organisations"
      />

      <CTASection
        eyebrow="Organisational enquiry"
        title="Discuss support for your organisation."
        intro="Tell us what you need to improve: recruitment insight, bespoke training, learning delivery, competence management, assurance evidence or safety-critical development. We can discuss the right UKTDA, TRAC-Learn, TRAC-CMS or RAT-BAT approach."
        primary={{
          label: "Discuss organisational support",
          to: "/contact",
          testId: "organisations-cta-contact",
        }}
        secondary={{
          label: "Explore RAT-BAT",
          to: "/ratbat",
          testId: "organisations-cta-ratbat",
        }}
      />
    </>
  );
}